"use client";

import React from 'react';
import { Mail } from "lucide-react";

interface ModalNewsletterProps {
    storeName: string;
}

export default function ModalNewsletter({ storeName }: ModalNewsletterProps) {
    const [email, setEmail] = React.useState("");
    const [status, setStatus] = React.useState<"idle" | "loading" | "success" | "error">("idle");
    const [message, setMessage] = React.useState("");
    const [mounted, setMounted] = React.useState(false);

    React.useEffect(() => {
        setMounted(true);
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;

        setStatus("loading");
        setMessage("");

        try {
            const res = await fetch("/api/subscribe", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, source: `${storeName} Modal` }),
            });

            const data = await res.json();

            if (!res.ok) throw new Error(data.error || "Failed to subscribe");

            setStatus("success");
            setMessage(data.message);
            setEmail("");
        } catch (error: any) {
            setStatus("error");
            setMessage(error.message);
        }
    };

    const inputId = React.useId();

    if (status === "success") {
        return (
            <div className="sp-newsletter sp-newsletter--modal" role="status">
                <p className="sp-newsletter__title">Thanks for subscribing!</p>
                <p className="sp-newsletter__hint">We&apos;ll send {storeName} coupons to your inbox.</p>
            </div>
        );
    }

    return (
        <section className="sp-newsletter sp-newsletter--modal" aria-label={`${storeName} coupon alerts`}>
            <p className="sp-newsletter__title">
                <Mail aria-hidden="true" />
                <span>Get {storeName} alerts</span>
            </p>
            <form className="sp-newsletter__form" onSubmit={handleSubmit}>
                <label htmlFor={inputId} className="sr-only">Email address</label>
                {mounted ? (
                    <input
                        id={inputId}
                        type="email"
                        name="email"
                        autoComplete="email"
                        inputMode="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Email address"
                        disabled={status === "loading"}
                        className="sp-newsletter__input"
                    />
                ) : (
                    <div className="sp-newsletter__input" aria-hidden="true" />
                )}
                <button type="submit" disabled={status === "loading"} className="sp-newsletter__button">
                    {status === "loading" ? "..." : "Go"}
                </button>
            </form>
            {message && status === "error" && (
                <p role="alert" className="sp-newsletter__message" data-status="error">{message}</p>
            )}
        </section>
    );
}
